/**
 * postMessage contract between the Google Drive folder-picker popup
 * (/google-drive-picker, hosted HERE on the warbler origin) and its opener
 * (the embedded Atlas iframe).
 *
 * Why warbler hosts the picker: the Google Picker renders only for origins
 * registered as Authorized JavaScript origins on the Google OAuth client.
 * Warbler's origins are registered; Atlas's are not, so a picker declared from
 * the Atlas origin is refused (ERR_BLOCKED_BY_RESPONSE) even top-level.
 * The popup fetches the Google access token itself with warbler's own session,
 * so the token never crosses origins; the opener sends only the account email.
 *
 * Mirrors atlas frontend/src/app/setup/google-picker/pickerBridge.ts — the
 * `source` discriminator MUST stay identical on both sides.
 */

export const PICKER_MESSAGE_SOURCE = 'atlas-google-picker';

/**
 * Which picker the opener wants. Absent on the wire means 'folders' — an atlas
 * build predating files mode sends no mode at all, and the two repos deploy
 * independently.
 */
export type PickerMode = 'folders' | 'files';

/** The subset of a Google Picker doc the opener needs to build an attachment. */
export interface PickerFileDoc {
  id: string;
  name: string;
  mimeType: string;
  url?: string;
}

export interface PickerReadyMessage {
  type: 'PICKER_READY';
  source: typeof PICKER_MESSAGE_SOURCE;
}

export interface PickerInitEmailMessage {
  type: 'PICKER_INIT_EMAIL';
  source: typeof PICKER_MESSAGE_SOURCE;
  payload: { email: string; mode?: PickerMode; mimeTypes?: string[] };
}

export interface PickerResultMessage {
  type: 'PICKER_RESULT';
  source: typeof PICKER_MESSAGE_SOURCE;
  payload: { folderId: string; folderName: string };
}

/**
 * Files-mode result. Deliberately a separate type from PICKER_RESULT rather
 * than an optional field on it: the folder guard stays total and the folder
 * flow — already shipped and browser-verified — carries no regression risk.
 */
export interface PickerFilesResultMessage {
  type: 'PICKER_FILES_RESULT';
  source: typeof PICKER_MESSAGE_SOURCE;
  // Key is `docs`, not `files` — the atlas opener's isPickerFilesResult guard
  // (frontend/src/app/setup/google-picker/pickerBridge.ts) reads payload.docs.
  payload: { docs: PickerFileDoc[] };
}

/** Popup → opener: mounted and waiting for the account email. */
export function pickerReady(): PickerReadyMessage {
  return { type: 'PICKER_READY', source: PICKER_MESSAGE_SOURCE };
}

/** Popup → opener: the folder the user chose. */
export function pickerResult(folderId: string, folderName: string): PickerResultMessage {
  return { type: 'PICKER_RESULT', source: PICKER_MESSAGE_SOURCE, payload: { folderId, folderName } };
}

/** Popup → opener: every file the user picked, in picked order. */
export function pickerFilesResult(files: PickerFileDoc[]): PickerFilesResultMessage {
  return { type: 'PICKER_FILES_RESULT', source: PICKER_MESSAGE_SOURCE, payload: { docs: files } };
}

/**
 * Which picker to show. Anything that is not exactly 'files' — absent,
 * mis-cased, or garbage — degrades to the folder picker: a harmless UX detour,
 * where trusting an arbitrary string would let a malformed message steer the UI.
 */
export function readPickerMode(payload?: { mode?: unknown }): PickerMode {
  return payload?.mode === 'files' ? 'files' : 'folders';
}

/**
 * The MIME filter for files mode, with non-string entries dropped so nothing
 * malformed reaches the Picker API. An empty result means "do not filter" —
 * the picker shows every file rather than silently showing none.
 */
export function readMimeTypes(payload?: { mimeTypes?: unknown }): string[] {
  const raw = payload?.mimeTypes;
  if (!Array.isArray(raw)) { return []; }
  return raw.filter((m): m is string => typeof m === 'string' && m.length > 0);
}

/** Opener → popup: which connected Google account to fetch a token for. */
export function isPickerInitEmail(data: unknown): data is PickerInitEmailMessage {
  if (typeof data !== 'object' || data === null) { return false; }
  const m = data as { type?: unknown; source?: unknown; payload?: unknown };
  if (m.type !== 'PICKER_INIT_EMAIL' || m.source !== PICKER_MESSAGE_SOURCE) { return false; }
  const payload = m.payload as { email?: unknown } | undefined;
  return typeof payload?.email === 'string' && payload.email.length > 0;
}

/**
 * Map a raw Picker PICKED payload to the docs the opener gets.
 *
 * Kept out of the view so multi-select mapping is unit-testable without gapi:
 * files mode must return *every* doc, where the folder flow deliberately takes
 * only the first. Entries missing an id or name are dropped rather than
 * forwarded half-built.
 */
export function pickedFileDocs(docs: unknown): PickerFileDoc[] {
  if (!Array.isArray(docs)) { return []; }
  return docs
    .filter((d: any) => d && typeof d.id === 'string' && d.id.length > 0
      && typeof d.name === 'string' && d.name.length > 0)
    .map((d: any) => {
      const doc: PickerFileDoc = {
        id: d.id,
        name: d.name,
        mimeType: typeof d.mimeType === 'string' ? d.mimeType : '',
      };
      if (typeof d.url === 'string' && d.url.length > 0) { doc.url = d.url; }
      return doc;
    });
}

/**
 * The origin the opener (embedded Atlas) runs on — messages are sent to and
 * accepted from exactly this origin. Same env + default as AtlasEmbed.vue.
 */
export function resolveAtlasOrigin(raw?: string): string {
  return new URL(raw || 'http://localhost:3000').origin;
}

/**
 * Minimum gap between picker builds — suppresses the initial build's own
 * focus tick and rapid double-rebuilds.
 */
export const REBUILD_MIN_INTERVAL_MS = 3000;

/**
 * How long after a rebuild disposes the old picker a CANCEL is treated as the
 * dispose's own echo rather than the merchant's click. The picker drives its
 * iframe over postMessage, so that CANCEL can land a tick or more after the
 * rebuild has finished — a plain "am I rebuilding right now" flag is already
 * false by then and the popup would close under the merchant.
 */
export const REBUILD_CANCEL_GRACE_MS = 1500;

/**
 * Decide whether the popup should rebuild its picker on this poll tick.
 *
 * Rebuilding matters after the merchant signs in to Google in ANOTHER window:
 * the popup's picker is stuck on its sign-in shell, and Google's own
 * continuation window cannot deliver a pick (its RPC relay targets the wrong
 * origin by construction). A rebuild here renders the now-authenticated picker
 * where the relay works. The trigger is the window regaining focus — a
 * false→true `document.hasFocus()` edge — because `focus` events are swallowed
 * when element focus sits inside the cross-origin picker iframe.
 */
export function shouldRebuildOnReturn(input: {
  prevHasFocus: boolean;
  hasFocus: boolean;
  msSinceLastBuild: number;
  picked: boolean;
}): boolean {
  if (input.picked) { return false; }
  if (!input.hasFocus || input.prevHasFocus) { return false; }
  return input.msSinceLastBuild >= REBUILD_MIN_INTERVAL_MS;
}

/**
 * Google account-chooser URL pre-selected to the account whose token the
 * picker uses. The Email param is Google's standard login hint — the picker
 * only renders when the BROWSER is signed in as exactly that account, so
 * steering the chooser prevents the wrong-account dead end. `continue` lands
 * on a harmless Google page: the picker's own continuation window cannot
 * deliver a pick (RPC origin mismatch), and non-Google continue URLs are
 * rejected by accounts.google.com anyway.
 */
export function googleSignInUrl(email: string): string {
  return 'https://accounts.google.com/AccountChooser?Email=' + encodeURIComponent(email)
    + '&continue=' + encodeURIComponent('https://myaccount.google.com');
}
